import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, MessageSquare, ExternalLink, Link2, Share2, Info, ShoppingCart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const WhatsappGuide = () => {
  const navigate = useNavigate();

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8 animate-fade-in">
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/dashboard')}
            className="flex items-center gap-2 -ml-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Kembali
          </Button>
        </div>

        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shadow-sm shrink-0">
              <MessageSquare className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">Petunjuk Menghubungkan WhatsApp API</h1>
              <p className="text-sm md:text-lg text-muted-foreground mt-1">
                Gunakan Chatbaze sebagai "otak" AI untuk WhatsApp API Anda (Go WhatsApp atau layanan serupa).
              </p>
            </div>
          </div>
        </div>

        <div className="bg-primary/5 rounded-xl p-6 border border-primary/10 flex items-start gap-4">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Info className="h-5 w-5 text-primary" />
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Untuk menggunakan aplikasi ini dengan WhatsApp API (seperti dari <strong>Go WhatsApp</strong> atau layanan serupa), Anda dapat memanfaatkan fitur <strong>REST API Data Connector</strong> yang sudah tersedia di Chatbaze. 
            Secara teknis, chatbot ini akan berfungsi sebagai "otak" yang memproses jawaban, sementara WhatsApp API Anda berfungsi sebagai "saluran" pengirimnya.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
              <Link2 className="h-6 w-6 text-primary" />
              1. Membaca Data WhatsApp
            </h2>
            <Card className="border-primary/10 shadow-sm">
              <CardContent className="pt-6 space-y-4 text-sm md:text-base text-muted-foreground">
                <p>Gunakan <strong>Data Connector</strong> jika chatbot butuh data real-time (misal: cek status pesanan):</p>
                <ol className="list-decimal pl-6 space-y-3">
                  <li>Masuk ke <strong className="text-foreground">Knowledge Base &gt; Data Connector</strong>.</li>
                  <li>Klik <strong className="text-foreground">Tambah Connector</strong> &gt; <strong className="text-foreground">REST API</strong>.</li>
                  <li>Masukkan <strong className="text-foreground">API URL</strong> (misal: <code>https://api.wa.com/v1/data</code>).</li>
                  <li>Masukkan <strong className="text-foreground">Headers</strong> (seperti <code>Authorization</code>).</li>
                  <li>Chatbot sekarang bisa mengambil data untuk menjawab pertanyaan user secara real-time.</li>
                </ol>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
              <Share2 className="h-6 w-6 text-primary" />
              2. Mengirim Jawaban ke WhatsApp
            </h2>
            <Card className="border-primary/10 shadow-sm">
              <CardContent className="pt-6 space-y-4 text-sm md:text-base text-muted-foreground">
                <p>Integrasi <strong>Webhook</strong> diperlukan agar chatbot membalas pesan otomatis:</p>
                <ol className="list-decimal pl-6 space-y-3">
                  <li>Cari pengaturan <strong className="text-foreground">Webhook URL</strong> di aplikasi WhatsApp API Anda.</li>
                  <li>Gunakan skrip "jembatan" untuk meneruskan pesan ke Chatbaze (<code>/api/chat</code>).</li>
                  <li>Server Anda kemudian mengirim balik jawaban AI menggunakan perintah <code>send_message</code> dari API WhatsApp Anda.</li>
                </ol>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-xl p-6 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-sm mt-8 animate-in slide-in-from-bottom-4 duration-700">
          <div className="flex items-start gap-4 text-left">
            <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center shrink-0">
              <ShoppingCart className="h-6 w-6 text-green-600" />
            </div>
            <div className="space-y-1">
              <h3 className="font-bold text-green-900">Butuh Server Gowa WhatsApp?</h3>
              <p className="text-sm text-green-700 leading-relaxed">
                Gunakan layanan <strong>Sumopod</strong> untuk mendapatkan server WhatsApp multi-device yang stabil, cepat, dan siap pakai. Cocok untuk integrasi Chatbaze Anda.
              </p>
            </div>
          </div>
          <Button 
            onClick={() => window.open('https://sumopod.com/register?ref=0df70865-08ae-4063-b5d4-bd201b10bc34', '_blank')}
            className="bg-green-600 hover:bg-green-700 text-white px-8 font-bold whitespace-nowrap h-11 transition-all hover:scale-105"
          >
            Daftar di Sumopod
          </Button>
        </div>

        <div className="space-y-6 pt-8 border-t">
          <h2 className="text-2xl font-bold text-foreground">Alur Kerja Webhook</h2>
          <div className="bg-muted/30 border rounded-xl p-8 space-y-8 shadow-inner">
            <div className="grid sm:grid-cols-4 gap-4 relative">
              <div className="hidden sm:block absolute top-1/2 left-0 right-0 h-0.5 bg-primary/20 -translate-y-1/2 z-0"></div>
              
              <div className="relative z-10 bg-background p-4 rounded-lg border shadow-sm text-center">
                <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-2 text-xs font-bold">1</div>
                <p className="text-[11px] font-medium leading-tight">Pesan masuk ke WhatsApp</p>
              </div>
              
              <div className="relative z-10 bg-background p-4 rounded-lg border shadow-sm text-center">
                <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-2 text-xs font-bold">2</div>
                <p className="text-[11px] font-medium leading-tight">Server Anda panggil API Chatbaze</p>
              </div>
              
              <div className="relative z-10 bg-background p-4 rounded-lg border shadow-sm text-center">
                <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-2 text-xs font-bold">3</div>
                <p className="text-[11px] font-medium leading-tight">AI Chatbaze proses jawaban</p>
              </div>
              
              <div className="relative z-10 bg-background p-4 rounded-lg border shadow-sm text-center">
                <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-2 text-xs font-bold">4</div>
                <p className="text-[11px] font-medium leading-tight">Kirim balik ke User via API WA</p>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-8 flex flex-col items-center gap-4">
          <Button 
            variant="outline" 
            onClick={() => window.open('https://api.chatbaze.id', '_blank')}
            className="flex items-center gap-2"
          >
            Buka Dokumentasi API
            <ExternalLink className="h-4 w-4" />
          </Button>
          <p className="text-xs text-muted-foreground">
            Butuh bantuan teknis? Hubungi tim support kami melalui chat di pojok kanan bawah.
          </p>
      </div>
    </div>
  );
};

export default WhatsappGuide;
